import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';

import {
  TGroupStatusOption,
  TGroupsFlexiblePlanCriteriaOption,
  TGroupsSubscriptionPlanCriteriaOption,
  groupFlexiblePlanCriteriaOptions,
  groupStatusOptions,
  groupSubscriptionPlanCriteriaOptions,
} from '../../../../../../constants';
import {
  adminCreateGroup,
  adminUpdateGroup,
  GROUPS_FLEXIBLE_PLAN_CRITERIAS,
  GROUPS_SUBSCRIPTION_PLAN_CRITERIAS,
  IGroup,
  TAdminCreateGroup,
  TAdminUpdateGroup,
} from '../../../../../../store/V2/GroupsSlice';
import { IFlexiblePlan } from '../../../../../../store/V2/FlexiblePlansSlice';
import { ISubscriptionPlan } from '../../../../../../store/V2/SubscriptionPlansSlice';
import { capitalizeFirstLetter } from '../../../../../../helper';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import FlexiblePlanListDropDown, {
  TFlexiblePlanOption,
} from '../../../../../../components/Inputs/admin/FlexiblePlanListDropDown';
import SubscriptionPlanListDropDown, {
  TSubscriptionPlanOption,
} from '../../../../../../components/Inputs/admin/SubscriptionPlanListDropDown';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';

interface AddGroupModalProps {
  open: boolean;
  onClose: () => void;
  group?: IGroup;
  readOnly?: boolean;
  duplicate?: boolean;
}

type TState = {
  name: string;
  status: TGroupStatusOption;
  rescheduleChecked: boolean;
  cancellationChecked: boolean;
  oneTimePaymentChecked: boolean;
  flexiblePlanChecked: boolean;
  subscriptionPlanChecked: boolean;
  selectedFlexiblePlanCriteria: TGroupsFlexiblePlanCriteriaOption;
  selectedFlexiblePlan: TFlexiblePlanOption | null;
  selectedFlexiblePlans: TFlexiblePlanOption[];
  selectedSubscriptionPlanCriteria: TGroupsSubscriptionPlanCriteriaOption;
  selectedSubscriptionPlan: TSubscriptionPlanOption | null;
  selectedSubscriptionPlans: TSubscriptionPlanOption[];
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  name: '',
  status: groupStatusOptions[0],
  rescheduleChecked: false,
  cancellationChecked: false,
  oneTimePaymentChecked: false,
  flexiblePlanChecked: false,
  subscriptionPlanChecked: false,
  selectedFlexiblePlanCriteria: groupFlexiblePlanCriteriaOptions[0],
  selectedFlexiblePlan: null,
  selectedFlexiblePlans: [],
  selectedSubscriptionPlanCriteria: groupSubscriptionPlanCriteriaOptions[0],
  selectedSubscriptionPlan: null,
  selectedSubscriptionPlans: [],
};

const AddGroupModal: FunctionComponent<AddGroupModalProps> = ({
  open,
  onClose,
  group,
  readOnly = false,
  duplicate = false,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  let mode: 'edit' | 'new' | 'view' | 'duplicate' = 'new';
  let modalTitle = 'Add New Group';

  const isSubmitDisabled =
    !state.name.trim().length ||
    !state.status ||
    (state.flexiblePlanChecked && !state.selectedFlexiblePlanCriteria) ||
    (state.flexiblePlanChecked &&
      state.selectedFlexiblePlanCriteria &&
      state.selectedFlexiblePlanCriteria.value !==
        GROUPS_FLEXIBLE_PLAN_CRITERIAS.ALL &&
      !state.selectedFlexiblePlans.length) ||
    (state.subscriptionPlanChecked &&
      !state.selectedSubscriptionPlanCriteria) ||
    (state.subscriptionPlanChecked &&
      state.selectedSubscriptionPlanCriteria &&
      state.selectedSubscriptionPlanCriteria.value !==
        GROUPS_SUBSCRIPTION_PLAN_CRITERIAS.ALL &&
      !state.selectedSubscriptionPlans.length);

  if (group) {
    mode = 'edit';
    modalTitle = 'Edit Group';

    if (readOnly) {
      mode = 'view';
      modalTitle = 'Group Details';
    } else if (duplicate) {
      mode = 'duplicate';
      modalTitle = 'Add New Group';
    }
  }

  useEffect(() => {
    if (mode !== 'new' && group) {
      setState((prevState) => {
        const newStateData = { ...prevState };
        const newSelectedFlexiblePlans: TFlexiblePlanOption[] = [];
        const newSelectedSubscriptionPlans: TSubscriptionPlanOption[] = [];

        if (group.name) {
          newStateData.name = group.name;
        }

        if (group.reschedule) {
          newStateData.rescheduleChecked = group.reschedule;
        }

        if (group.cancellation) {
          newStateData.cancellationChecked = group.cancellation;
        }

        if (group.oneTimePayment) {
          newStateData.oneTimePaymentChecked = group.oneTimePayment;
        }

        if (group.flexiblePlan) {
          newStateData.flexiblePlanChecked = group.flexiblePlan;

          if (group.flexiblePlanCriteria) {
            const matchingFlexiblePlanCriteria = groupFlexiblePlanCriteriaOptions.find(
              (item) => item.value === group.flexiblePlanCriteria
            );

            if (matchingFlexiblePlanCriteria) {
              newStateData.selectedFlexiblePlanCriteria = matchingFlexiblePlanCriteria;
            }

            if (
              group.flexiblePlanCriteria !==
                GROUPS_FLEXIBLE_PLAN_CRITERIAS.ALL &&
              group.flexiblePlans &&
              group.flexiblePlans.length
            ) {
              const _flexiblePlans = (group.flexiblePlans ||
                []) as IFlexiblePlan[];

              _flexiblePlans.forEach((item) => {
                newSelectedFlexiblePlans.push({
                  label: item.name,
                  value: item._id,
                });
              });
            }
          }
        }

        if (group.subscriptionPlan) {
          newStateData.subscriptionPlanChecked = group.subscriptionPlan;

          if (group.subscriptionPlanCriteria) {
            const matchingSubscriptionPlanCriteria = groupSubscriptionPlanCriteriaOptions.find(
              (item) => item.value === group.subscriptionPlanCriteria
            );

            if (matchingSubscriptionPlanCriteria) {
              newStateData.selectedSubscriptionPlanCriteria = matchingSubscriptionPlanCriteria;
            }

            if (
              group.subscriptionPlanCriteria !==
                GROUPS_SUBSCRIPTION_PLAN_CRITERIAS.ALL &&
              group.subscriptionPlans &&
              group.subscriptionPlans.length
            ) {
              const _subscriptionPlans = (group.subscriptionPlans ||
                []) as ISubscriptionPlan[];

              _subscriptionPlans.forEach((item) => {
                newSelectedSubscriptionPlans.push({
                  label: item.name,
                  value: item._id,
                  billingPeriod: item.billingPeriod,
                });
              });
            }
          }
        }

        if (group.status) {
          const matchingStatus = groupStatusOptions.find(
            (item) => item.value === group._id
          );

          if (matchingStatus) {
            newStateData.status = matchingStatus;
          }
        }

        newStateData.selectedFlexiblePlans = newSelectedFlexiblePlans;
        newStateData.selectedSubscriptionPlans = newSelectedSubscriptionPlans;

        return newStateData;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleAddFlexiblePlan = () => {
    setState((prevState) => {
      const newStateData = { ...prevState, selectedFlexiblePlan: null };

      if (
        state.selectedFlexiblePlan &&
        !newStateData.selectedFlexiblePlans.some(
          (item) =>
            state.selectedFlexiblePlan &&
            item.value === state.selectedFlexiblePlan.value
        )
      ) {
        newStateData.selectedFlexiblePlans = [
          ...prevState.selectedFlexiblePlans,
          state.selectedFlexiblePlan,
        ];
      }

      return newStateData;
    });
  };

  const removeFlexiblePlan = (course: TFlexiblePlanOption) => {
    setState((prevState) => {
      const newStateData = { ...prevState };

      newStateData.selectedFlexiblePlans = [
        ...prevState.selectedFlexiblePlans,
      ].filter((item) => item.value !== course.value);

      return newStateData;
    });
  };

  const handleAddSubscriptionPlan = () => {
    setState((prevState) => {
      const newStateData = { ...prevState, selectedSubscriptionPlan: null };

      if (
        state.selectedSubscriptionPlan &&
        !newStateData.selectedSubscriptionPlans.some(
          (item) =>
            state.selectedSubscriptionPlan &&
            item.value === state.selectedSubscriptionPlan.value
        )
      ) {
        newStateData.selectedSubscriptionPlans = [
          ...prevState.selectedSubscriptionPlans,
          state.selectedSubscriptionPlan,
        ];
      }

      return newStateData;
    });
  };

  const removeSubscriptionPlan = (course: TSubscriptionPlanOption) => {
    setState((prevState) => {
      const newStateData = { ...prevState };

      newStateData.selectedSubscriptionPlans = [
        ...prevState.selectedSubscriptionPlans,
      ].filter((item) => item.value !== course.value);

      return newStateData;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (mode === 'new' || mode === 'duplicate') {
      const apiData: TAdminCreateGroup = {
        name: state.name,
        status: state.status.value,
        reschedule: state.rescheduleChecked,
        cancellation: state.cancellationChecked,
        oneTimePayment: state.oneTimePaymentChecked,
        flexiblePlan: state.flexiblePlanChecked,
        subscriptionPlan: state.subscriptionPlanChecked,
      };

      if (state.flexiblePlanChecked) {
        if (state.selectedFlexiblePlanCriteria) {
          apiData.flexiblePlanCriteria =
            state.selectedFlexiblePlanCriteria.value;

          if (
            state.selectedFlexiblePlanCriteria.value !==
              GROUPS_FLEXIBLE_PLAN_CRITERIAS.ALL &&
            state.selectedFlexiblePlans.length
          ) {
            apiData.flexiblePlans = state.selectedFlexiblePlans.map(
              (item) => item.value
            );
          }
        }
      }

      if (state.subscriptionPlanChecked) {
        if (state.selectedSubscriptionPlanCriteria) {
          apiData.subscriptionPlanCriteria =
            state.selectedSubscriptionPlanCriteria.value;

          if (
            state.selectedSubscriptionPlanCriteria.value !==
              GROUPS_SUBSCRIPTION_PLAN_CRITERIAS.ALL &&
            state.selectedSubscriptionPlans.length
          ) {
            apiData.subscriptionPlans = state.selectedSubscriptionPlans.map(
              (item) => item.value
            );
          }
        }
      }

      const res: any = await dispatch(adminCreateGroup(apiData));

      if (res.payload) {
        onClose();
      }
    } else if (mode === 'edit' && group) {
      const apiData: TAdminUpdateGroup = {
        id: group._id,
        name: state.name,
        status: state.status.value,
        reschedule: state.rescheduleChecked,
        cancellation: state.cancellationChecked,
        oneTimePayment: state.oneTimePaymentChecked,
        flexiblePlan: state.flexiblePlanChecked,
        subscriptionPlan: state.subscriptionPlanChecked,
      };

      if (state.flexiblePlanChecked) {
        if (state.selectedFlexiblePlanCriteria) {
          apiData.flexiblePlanCriteria =
            state.selectedFlexiblePlanCriteria.value;

          if (
            state.selectedFlexiblePlanCriteria.value !==
              GROUPS_FLEXIBLE_PLAN_CRITERIAS.ALL &&
            state.selectedFlexiblePlans.length
          ) {
            apiData.flexiblePlans = state.selectedFlexiblePlans.map(
              (item) => item.value
            );
          }
        }
      }

      if (state.subscriptionPlanChecked) {
        if (state.selectedSubscriptionPlanCriteria) {
          apiData.subscriptionPlanCriteria =
            state.selectedSubscriptionPlanCriteria.value;

          if (
            state.selectedSubscriptionPlanCriteria.value !==
              GROUPS_SUBSCRIPTION_PLAN_CRITERIAS.ALL &&
            state.selectedSubscriptionPlans.length
          ) {
            apiData.subscriptionPlans = state.selectedSubscriptionPlans.map(
              (item) => item.value
            );
          }
        }
      }

      const res: any = await dispatch(adminUpdateGroup(apiData));

      if (res.payload) {
        onClose();
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {modalTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  label="Name *"
                  placeholder="Enter here..."
                  value={state.name}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({ name: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  disableClearable
                  fullWidth
                  size="small"
                  readOnly={readOnly}
                  value={state.status}
                  options={groupStatusOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  onChange={(event, value) =>
                    updateStateAttributes({ status: value })
                  }
                  getOptionLabel={(option) =>
                    capitalizeFirstLetter(option.label)
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Status *"
                      placeholder="Status"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <Stack>
                  <FormControlLabel
                    label="Reschedule"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.rescheduleChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({ rescheduleChecked: value })
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    label="Cancellation"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.cancellationChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            cancellationChecked: value,
                          })
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    label="One Time Payment"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.oneTimePaymentChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            oneTimePaymentChecked: value,
                          })
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    label="Flexible Plan"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.flexiblePlanChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            flexiblePlanChecked: value,
                          })
                        }
                      />
                    }
                  />
                  {state.flexiblePlanChecked ? (
                    <Collapse in>
                      <Stack gap="15px" p="15px 0">
                        <Autocomplete
                          disableClearable
                          fullWidth
                          size="small"
                          readOnly={readOnly}
                          value={state.selectedFlexiblePlanCriteria}
                          options={groupFlexiblePlanCriteriaOptions}
                          popupIcon={TPAutoCompleteArrowIconComponent}
                          onChange={(event, value) =>
                            updateStateAttributes({
                              selectedFlexiblePlanCriteria: value,
                            })
                          }
                          getOptionLabel={(option) =>
                            capitalizeFirstLetter(option.label)
                          }
                          renderInput={(params) => (
                            <TPTextField
                              {...params}
                              {...textFieldProps}
                              size="small"
                              label="Criteria *"
                              placeholder="Select"
                            />
                          )}
                        />
                        {state.selectedFlexiblePlanCriteria.value !==
                        GROUPS_FLEXIBLE_PLAN_CRITERIAS.ALL ? (
                          <>
                            {mode !== 'view' ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap="10px"
                              >
                                <FlexiblePlanListDropDown
                                  selectedItem={state.selectedFlexiblePlan}
                                  handleChange={(event, value) =>
                                    updateStateAttributes({
                                      selectedFlexiblePlan: value,
                                    })
                                  }
                                  componentProps={{ fullWidth: true }}
                                  textFieldProps={{
                                    label: 'Flexible Plan',
                                    placeholder: 'Select',
                                    ...textFieldProps,
                                  }}
                                />
                                <DarkButton
                                  variant="contained"
                                  color="inherit"
                                  sx={{ ...defaultButtonStyles }}
                                  onClick={handleAddFlexiblePlan}
                                  disabled={!state.selectedFlexiblePlan}
                                >
                                  Add
                                </DarkButton>
                              </Stack>
                            ) : null}
                            <Stack gap="20px">
                              {state.selectedFlexiblePlans.map((item) => {
                                return (
                                  <Stack
                                    key={item.value}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Typography
                                      fontSize="14px"
                                      fontWeight={500}
                                    >
                                      {item.label}
                                    </Typography>
                                    {mode !== 'view' ? (
                                      <IconButton
                                        onClick={() => removeFlexiblePlan(item)}
                                      >
                                        <DeleteIcon sx={{ color: '#1E293B' }} />
                                      </IconButton>
                                    ) : null}
                                  </Stack>
                                );
                              })}
                            </Stack>
                          </>
                        ) : null}
                      </Stack>
                    </Collapse>
                  ) : null}
                  <FormControlLabel
                    label="Subscription Plan"
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={state.subscriptionPlanChecked}
                        onChange={(event, value) =>
                          updateStateAttributes({
                            subscriptionPlanChecked: value,
                          })
                        }
                      />
                    }
                  />
                  {state.subscriptionPlanChecked ? (
                    <Collapse in>
                      <Stack gap="15px" p="15px 0">
                        <Autocomplete
                          disableClearable
                          fullWidth
                          size="small"
                          readOnly={readOnly}
                          value={state.selectedSubscriptionPlanCriteria}
                          options={groupSubscriptionPlanCriteriaOptions}
                          popupIcon={TPAutoCompleteArrowIconComponent}
                          onChange={(event, value) =>
                            updateStateAttributes({
                              selectedSubscriptionPlanCriteria: value,
                            })
                          }
                          getOptionLabel={(option) =>
                            capitalizeFirstLetter(option.label)
                          }
                          renderInput={(params) => (
                            <TPTextField
                              {...params}
                              {...textFieldProps}
                              size="small"
                              label="Criteria *"
                              placeholder="Select"
                            />
                          )}
                        />
                        {state.selectedSubscriptionPlanCriteria.value !==
                        GROUPS_SUBSCRIPTION_PLAN_CRITERIAS.ALL ? (
                          <>
                            {mode !== 'view' ? (
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap="10px"
                              >
                                <SubscriptionPlanListDropDown
                                  selectedItem={state.selectedSubscriptionPlan}
                                  handleChange={(event, value) =>
                                    updateStateAttributes({
                                      selectedSubscriptionPlan: value,
                                    })
                                  }
                                  componentProps={{ fullWidth: true }}
                                  textFieldProps={{
                                    label: 'Subscription Plan',
                                    placeholder: 'Select',
                                    ...textFieldProps,
                                  }}
                                />
                                <DarkButton
                                  variant="contained"
                                  color="inherit"
                                  sx={{ ...defaultButtonStyles }}
                                  onClick={handleAddSubscriptionPlan}
                                  disabled={!state.selectedSubscriptionPlan}
                                >
                                  Add
                                </DarkButton>
                              </Stack>
                            ) : null}
                            <Stack gap="20px">
                              {state.selectedSubscriptionPlans.map((item) => {
                                return (
                                  <Stack
                                    key={item.value}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Typography
                                      fontSize="14px"
                                      fontWeight={500}
                                    >
                                      {item.label}
                                    </Typography>
                                    {mode !== 'view' ? (
                                      <IconButton
                                        onClick={() =>
                                          removeSubscriptionPlan(item)
                                        }
                                      >
                                        <DeleteIcon sx={{ color: '#1E293B' }} />
                                      </IconButton>
                                    ) : null}
                                  </Stack>
                                );
                              })}
                            </Stack>
                          </>
                        ) : null}
                      </Stack>
                    </Collapse>
                  ) : null}
                </Stack>
              </FormGroup>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default AddGroupModal;
